/**
 * The map pin of a location, as the admin forms enter it.
 *
 * Optional everywhere (PO, 2026-10-16): the customer map falls back to the
 * address text without one, and a field that has to be filled gets a guessed
 * pin, which is worse than none. When entered, both values are needed — one
 * coordinate alone is no place. The server asks the same question
 * (`coordinatesError`, new-backend `lib/location/locationWrite.ts`).
 */

export interface Coordinates {
    geoLat: number | null;
    geoLng: number | null;
}

export type CoordinatesState = 'complete' | 'missing' | 'incomplete';

export const coordinatesState = ({ geoLat, geoLng }: Coordinates): CoordinatesState => {
    if (geoLat == null && geoLng == null) return 'missing';
    if (geoLat == null || geoLng == null) return 'incomplete';
    return 'complete';
};

/**
 * A Google-Maps-style "lat, lng" paste. Only dot-decimal numbers separated by
 * a comma, which is how Maps copies them regardless of the user's locale.
 */
export const parseCoordinatePaste = (val: string): { lat: number, lng: number } | null => {
    const match = /^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)$/.exec(val.trim());
    if (!match) return null;
    const lat = Number.parseFloat(match[1]);
    const lng = Number.parseFloat(match[2]);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
    return { lat, lng };
};

/**
 * The value one field holds after typing or pasting into it. A pasted pair
 * fills both fields; an empty field clears its own value; anything that is not
 * a number leaves the stored value as it was.
 */
export const coordinatesAfterInput = (
    current: Coordinates, field: 'geoLat' | 'geoLng', val: string,
): Coordinates => {
    const paste = parseCoordinatePaste(val);
    if (paste) return { geoLat: paste.lat, geoLng: paste.lng };
    if (val.trim() === '') return { ...current, [field]: null };
    const parsed = Number.parseFloat(val);
    return Number.isNaN(parsed) ? current : { ...current, [field]: parsed };
};

/**
 * The preview map, the same embed the customer app shows — keyless, so no new
 * service. Null without a complete pin.
 */
export const coordinatesPreviewSrc = (coordinates: Coordinates): string | null => {
    if (coordinatesState(coordinates) !== 'complete') return null;
    const query = encodeURIComponent([coordinates.geoLat, coordinates.geoLng].join(','));
    return `https://www.google.com/maps?q=${query}&z=16&output=embed`;
};

/** The address in Google Maps, where the operator copies the coordinates from. */
export const mapsSearchUrl = (parts: Array<string | null | undefined>): string => {
    const query = parts.map((part) => (part ?? '').trim()).filter(Boolean).join(' ');
    return query
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`
        : 'https://www.google.com/maps';
};
